import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { toast } from 'sonner';
import {
  TrendingUp, PlayCircle, Calendar, ChevronDown, AlertTriangle,
  DollarSign, PieChart, Shield, Flame, Target, Lightbulb
} from 'lucide-react';

const periodOptions = [
  { value: 'last_7_days', label: 'Últimos 7 días', days: 7 },
  { value: 'last_15_days', label: 'Últimos 15 días', days: 15 },
  { value: 'last_30_days', label: 'Últimos 30 días', days: 30 },
];

function DonutChart({ killers, regulares, criticos }) {
  const total = killers + regulares + criticos;
  if (!total) {
    return <div className="flex items-center gap-6 text-sm text-slate-500">Sin publicaciones con COGS para clasificar</div>;
  }
  const kPct = (killers / total) * 100;
  const rPct = (regulares / total) * 100;
  return (
    <div className="flex items-center gap-6">
      <div className="relative w-28 h-28">
        <svg viewBox="0 0 36 36" className="w-full h-full">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#1e293b" strokeWidth="3" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#22c55e" strokeWidth="3"
            strokeDasharray={`${kPct} ${100 - kPct}`} strokeDashoffset="25" strokeLinecap="round" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f59e0b" strokeWidth="3"
            strokeDasharray={`${rPct} ${100 - rPct}`} strokeDashoffset={`${25 - kPct}`} strokeLinecap="round" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#ef4444" strokeWidth="3"
            strokeDasharray={`${100 - kPct - rPct} ${kPct + rPct}`} strokeDashoffset={`${25 - kPct - rPct}`} strokeLinecap="round" />
        </svg>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span className="text-slate-400">KILLERS</span>
          <span className="text-white font-bold ml-auto">{killers}</span>
          <span className="text-xs text-slate-500">&gt;30%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <span className="text-slate-400">REGULARES</span>
          <span className="text-white font-bold ml-auto">{regulares}</span>
          <span className="text-xs text-slate-500">15-29%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span className="text-slate-400">CRÍTICOS</span>
          <span className="text-white font-bold ml-auto">{criticos}</span>
          <span className="text-xs text-slate-500">&lt;15%</span>
        </div>
      </div>
    </div>
  );
}

export default function ProfitabilityPage() {
  const [period, setPeriod] = useState('last_30_days');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showPeriodMenu, setShowPeriodMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('killers');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingCogs, setEditingCogs] = useState(null);
  const [cogsValue, setCogsValue] = useState('');
  const [cogsError, setCogsError] = useState('');
  const [stats, setStats] = useState({ totalMargin: 0, killers: 0, regulares: 0, criticos: 0, sinCogs: 0, totalProducts: 0, withCogs: 0, withoutCogs: 0 });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const days = periodOptions.find(p => p.value === period)?.days || 30;
      const params = { period: days, sort_by: 'revenue' };
      if (period === 'custom' && customFrom && customTo) {
        params.date_from = new Date(`${customFrom}T00:00:00`).toISOString();
        params.date_to = new Date(`${customTo}T23:59:59.999`).toISOString();
      }
      const res = await api.get('/profitability/products', { params });
      const prods = res.data.products || [];
      setProducts(prods);

      const summary = res.data.summary || {};
      const health = summary.portfolio_health || {};
      setStats({ totalMargin: summary.total_margin || 0, killers: health.killers || 0, regulares: health.regulares || 0, criticos: health.criticos || 0, sinCogs: health.sin_cogs || 0, totalProducts: res.data.total || prods.length, withCogs: summary.products_with_cogs || 0, withoutCogs: summary.products_without_cogs || 0 });
    } catch (err) {
      console.error('Error loading profitability:', err);
      setError('No se pudo cargar la rentabilidad. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [period, customFrom, customTo]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpdateCogs = async (productId, value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setCogsError('El COGS debe ser un número finito no negativo.');
      return;
    }
    try {
      await api.put(`/profitability/cogs/${productId}`, { cogs: parsed });
      toast.success('COGS actualizado');
      setEditingCogs(null);
      setCogsError('');
      await loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setCogsError(detail || 'Error al actualizar COGS');
    }
  };

  const beginCogsEdit = (prod) => {
    setEditingCogs(prod.id || prod.meli_id);
    setCogsValue(prod.cogs > 0 ? String(prod.cogs) : '');
    setCogsError('');
  };

  const formatCurrency = (v) => `${(v || 0) < 0 ? '-' : ''}$${Math.abs(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}`;
  const selectedLabel = period === 'custom'
    ? `${customFrom || 'Desde'} — ${customTo || 'Hasta'}`
    : periodOptions.find(p => p.value === period)?.label || 'Últimos 30 días';

  const filteredProducts = products.filter(p => {
    if (activeTab === 'killers') return p.classification === 'KILLER';
    if (activeTab === 'criticos') return p.classification === 'CRITICO';
    if (activeTab === 'oportunidades') return !p.has_cogs;
    if (activeTab === 'sin_cogs') return !p.has_cogs;
    return true;
  });

  const completionPct = stats.totalProducts > 0 ? Math.round((stats.withCogs / stats.totalProducts) * 100) : 0;

  return (
    <DashboardLayout
      title="Gestión de Rentabilidad"
      subtitle="Margen de Contribución por Publicación • Meta: >30%"
      icon={TrendingUp}
      backTo="/dashboard"
      headerRight={
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-3 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors">
            <PlayCircle size={16} />
            Ver tutorial
          </button>
          <div className="relative">
            <button
              data-testid="profitability-period"
              onClick={() => setShowPeriodMenu(!showPeriodMenu)}
              className="flex items-center gap-2 px-4 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors"
            >
              <Calendar size={16} />
              {selectedLabel}
              <ChevronDown size={14} />
            </button>
            {showPeriodMenu && (
              <div className="absolute right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-10 w-48">
                {periodOptions.map(p => (
                  <button
                    key={p.value}
                    onClick={() => { setPeriod(p.value); setShowPeriodMenu(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-700 ${period === p.value ? 'text-blue-400' : 'text-slate-300'}`}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  onClick={() => setPeriod('custom')}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-700 ${period === 'custom' ? 'text-blue-400' : 'text-slate-300'}`}
                >Personalizado</button>
                {period === 'custom' && (
                  <div className="border-t border-slate-700 p-3 space-y-2">
                    <label className="block text-xs text-slate-400">Desde
                      <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="mt-1 w-full rounded bg-slate-900 border border-slate-600 px-2 py-1 text-xs text-white" />
                    </label>
                    <label className="block text-xs text-slate-400">Hasta
                      <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="mt-1 w-full rounded bg-slate-900 border border-slate-600 px-2 py-1 text-xs text-white" />
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>
      ) : error ? (
        <div role="alert" className="rounded-xl border border-red-900/50 bg-red-950/20 p-6 text-red-300">{error}</div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Margen Total */}
            <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign size={18} className="text-emerald-400" />
                <div>
                  <h3 className="text-sm font-semibold text-white">Margen de Contribución Total</h3>
                  <p className="text-xs text-slate-500">Disponible para Gastos Operativos</p>
                </div>
              </div>
              <p className="text-3xl font-bold text-emerald-400 mt-3">{formatCurrency(stats.totalMargin)}</p>
              <p className="text-xs text-slate-500 mt-1">COGS vacío = 0 para comparar con Selltrix</p>
            </div>

            {/* Salud del Portafolio */}
            <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <PieChart size={18} className="text-blue-400" />
                <div>
                  <h3 className="text-sm font-semibold text-white">Salud del Portafolio</h3>
                  <p className="text-xs text-slate-500">Distribución por rentabilidad</p>
                </div>
              </div>
              <DonutChart killers={stats.killers} regulares={stats.regulares} criticos={stats.criticos} />
            </div>

            {/* Completitud de Costos */}
            <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <Shield size={18} className="text-amber-400" />
                <div>
                  <h3 className="text-sm font-semibold text-white">Completitud de Costos</h3>
                  <p className="text-xs text-slate-500">Integridad de datos COGS</p>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3">
                <div className="relative w-16 h-16">
                  <svg viewBox="0 0 36 36" className="w-full h-full">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#1e293b" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#22c55e" strokeWidth="3"
                      strokeDasharray={`${completionPct} ${100 - completionPct}`} strokeDashoffset="25" strokeLinecap="round" />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white">{completionPct}%</span>
                </div>
                <div>
                  <p className="text-sm text-white">Faltan <strong className="text-amber-400">{stats.totalProducts - stats.withCogs}</strong> productos</p>
                  <p className="text-xs text-slate-500">sin costo asignado</p>
                  <p className="text-xs text-slate-500">{stats.withCogs} de {stats.totalProducts} con COGS</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 mb-4">
            {[
              { id: 'killers', label: 'Top Killers', icon: Flame, count: stats.killers },
              { id: 'criticos', label: 'Críticos', icon: AlertTriangle, count: stats.criticos },
              { id: 'oportunidades', label: 'Oportunidades', icon: Lightbulb, count: stats.withoutCogs },
              { id: 'sin_cogs', label: 'Sin COGS', icon: Shield, count: stats.withoutCogs },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`profitability-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-slate-800 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <tab.icon size={15} />
                {tab.label}
                {tab.count > 0 && (
                  <span className="bg-slate-700 text-xs px-1.5 py-0.5 rounded">{tab.count}</span>
                )}
              </button>
            ))}
          </div>

          {/* Products Table */}
          <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800/50">
                  {['Publicación', 'Uds', 'Ingresos', 'Precio Prom', 'COGS', 'Comisión', 'Envío', 'Retenc.', 'Ads', 'Tráfico Ads', 'Margen $', 'Margen %', 'Diagnóstico'].map(h => (
                    <th key={h} className="py-3 px-3 text-xs text-slate-400 font-medium text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr><td colSpan={13} className="py-8 text-center text-slate-500">No hay productos en esta categoría</td></tr>
                ) : filteredProducts.map(prod => (
                  <tr key={prod.id || prod.meli_id} className="border-b border-slate-800/30 hover:bg-slate-800/20">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        {prod.thumbnail && <img src={prod.thumbnail} alt="" className="w-9 h-9 rounded object-cover bg-slate-800" />}
                        <div className="min-w-0">
                          <p className="text-white text-xs truncate max-w-[180px]">{prod.title}</p>
                          <p className="text-[10px] text-slate-500 font-mono">{prod.meli_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-white">{prod.units || 0}</td>
                    <td className="py-3 px-3 text-white">{formatCurrency(prod.revenue)}</td>
                    <td className="py-3 px-3 text-white">{formatCurrency(prod.avg_price)}</td>
                    <td className="py-3 px-3">
                      {editingCogs === (prod.id || prod.meli_id) ? (
                        <div className="flex items-center gap-2">
                          <input
                            aria-label={`COGS de ${prod.title}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={cogsValue}
                            onChange={event => setCogsValue(event.target.value)}
                            onKeyDown={event => {
                              if (event.key === 'Enter') handleUpdateCogs(prod.id || prod.meli_id, cogsValue);
                              if (event.key === 'Escape') { setEditingCogs(null); setCogsError(''); }
                            }}
                            className="w-24 px-2 py-1 bg-slate-800 border border-slate-700 rounded text-white text-xs"
                            autoFocus
                          />
                          <button onClick={() => handleUpdateCogs(prod.id || prod.meli_id, cogsValue)} className="text-xs text-emerald-400">Guardar</button>
                          <button onClick={() => { setEditingCogs(null); setCogsError(''); }} className="text-xs text-slate-400">Cancelar</button>
                          {cogsError && <span role="alert" className="text-xs text-red-400">{cogsError}</span>}
                        </div>
                      ) : prod.cogs > 0 ? (
                        <div className="flex items-center gap-2">
                          <span className="text-white">{formatCurrency(prod.cogs)}</span>
                          <button aria-label={`Editar COGS de ${prod.title}`} onClick={() => beginCogsEdit(prod)} className="text-xs text-blue-400">Editar</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => beginCogsEdit(prod)}
                          className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded text-xs hover:bg-blue-600/30"
                        >
                          Ingresar
                        </button>
                      )}
                    </td>
                    <td className="py-3 px-3 text-red-400">{prod.commission ? `-${formatCurrency(prod.commission)}` : '-'}</td>
                    <td className="py-3 px-3 text-red-400">{prod.shipping ? `-${formatCurrency(prod.shipping)}` : '-'}</td>
                    <td className="py-3 px-3 text-red-400">{prod.retention ? `-${formatCurrency(prod.retention)}` : '-'}</td>
                    <td title={prod.ads == null ? prod.ads_unavailable_reason || 'Dato no disponible' : undefined} className="py-3 px-3 text-red-400">{prod.ads != null && prod.ads !== 0 ? `-${formatCurrency(prod.ads)}` : '-'}</td>
                    <td className="py-3 px-3 text-slate-400">{prod.ads_traffic ? `${prod.ads_traffic}%` : '-'}</td>
                    <td className={`py-3 px-3 font-medium ${prod.margin_amount < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{formatCurrency(prod.margin_amount)}</td>
                    <td className="py-3 px-3">
                      <span className={`font-semibold ${
                        prod.classification === 'KILLER' ? 'text-emerald-400' :
                        prod.classification === 'REGULAR' ? 'text-amber-400' : prod.classification === 'SIN_COGS' ? 'text-slate-500' : 'text-red-400'
                      }`}>
                        {prod.classification === 'SIN_COGS' ? 'SIN COGS' : `${(prod.margin_pct || 0).toFixed(1)}%`}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      {prod.diagnostico ? (
                        <span className="flex items-center gap-1 text-xs text-amber-400">
                          <AlertTriangle size={12} aria-hidden="true" />
                          <span title={prod.accion}>{prod.diagnostico}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500" title="Sin recomendación">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
